import {
  DT,
  type Input,
  type MapData,
  type State,
  type Vec3,
} from "./types.js";
import { World, inBox, touchesTrigger } from "./collision.js";
import { add, clone, dot, scale, speed } from "./vector.js";
export const movement = {
  gravity: 800,
  jump: 301.993377,
  groundAcceleration: 10,
  airAcceleration: 100,
  friction: 4,
  stopSpeed: 80,
  maxWishSpeed: 250,
  airWishCap: 30,
  stepHeight: 18,
  autoBhop: true,
};
export function spawn(map: MapData): State {
  return {
    p: clone(map.spawn),
    v: [0, 0, 0],
    grounded: false,
    duck: false,
    tick: 0,
    startTick: -1,
    finishTick: -1,
    stage: 0,
    checkpoint: clone(map.spawn),
    startArmed:
      map.triggers?.some(
        (t) => t.type === "start" && touchesTrigger(map.spawn, t, 72),
      ) ?? false,
    splits: [],
    assisted: false,
    jumps: 0,
    distance: 0,
    maxSpeed: 0,
    lastJump: false,
    trigger: "",
    yaw: map.yaw,
    pitch: 0,
    deaths: 0,
  };
}
function accelerate(
  v: Vec3,
  wish: Vec3,
  wishSpeed: number,
  accel: number,
  cap = wishSpeed,
) {
  const extra = cap - dot(v, wish);
  if (extra > 0) {
    const amount = Math.min(extra, accel * wishSpeed * DT);
    for (let i = 0; i < 3; i++) v[i] += wish[i] * amount;
  }
}
function slide(
  world: World,
  p: Vec3,
  v: Vec3,
  height: number,
  tick: number,
): { p: Vec3; v: Vec3 } {
  let remaining = DT;
  const original = clone(v),
    normals: Vec3[] = [];
  for (let bump = 0; bump < 4; bump++) {
    const tr = world.trace(p, scale(v, remaining), height, tick);
    if (tr.solid) {
      v = [0, 0, 0];
      break;
    }
    p = add(p, scale(v, remaining * tr.fraction));
    if (tr.fraction === 1) break;
    remaining *= 1 - tr.fraction;
    normals.push(tr.normal);
    let candidate: Vec3 = [0, 0, 0];
    let found = false;
    for (const n of normals) {
      candidate = add(v, scale(n, -dot(v, n)));
      if (normals.every((other) => dot(candidate, other) >= -0.001)) {
        found = true;
        break;
      }
    }
    if (!found && normals.length === 2) {
      const [a, b] = normals;
      const c: Vec3 = [
        a[1] * b[2] - a[2] * b[1],
        a[2] * b[0] - a[0] * b[2],
        a[0] * b[1] - a[1] * b[0],
      ];
      const len = Math.hypot(...c);
      candidate = len > 0.001 ? scale(c, dot(v, c) / (len * len)) : [0, 0, 0];
    }
    v = candidate;
    if (dot(v, original) <= 0) {
      v = [0, 0, 0];
      break;
    }
  }
  return { p, v };
}
export function step(world: World, s: State, input: Input): State {
  s.tick++;
  s.yaw = input.yaw;
  s.pitch = input.pitch;
  if (s.grounded)
    for (const platform of world.map.platforms ?? []) {
      const old =
          Math.sin(((s.tick - 1) / 100 / platform.period) * Math.PI * 2) *
          platform.amplitude,
        next =
          Math.sin((s.tick / 100 / platform.period) * Math.PI * 2) *
          platform.amplitude;
      const min = add(platform.brush.min, scale(platform.axis, old)),
        max = add(platform.brush.max, scale(platform.axis, old));
      if (
        Math.abs(s.p[2] - max[2]) < 2.1 &&
        s.p[0] > min[0] - 16 &&
        s.p[0] < max[0] + 16 &&
        s.p[1] > min[1] - 16 &&
        s.p[1] < max[1] + 16
      ) {
        const delta = scale(platform.axis, next - old);
        const carry = world.trace(s.p, delta, s.duck ? 36 : 72, s.tick, true);
        s.p = add(s.p, scale(delta, carry.fraction));
        break;
      }
    }
  if (input.reset) {
    s.p = clone(s.checkpoint);
    s.v = [0, 0, 0];
    s.assisted = true;
    s.grounded = false;
  }
  if (input.duck) s.duck = true;
  else if (s.duck && !world.trace(s.p, [0, 0, 0], 72, s.tick).solid)
    s.duck = false;
  const height = s.duck ? 36 : 72;
  const ground = world.trace(s.p, [0, 0, -2], height, s.tick);
  s.grounded =
    s.v[2] <= 0 &&
    ground.fraction < 1 &&
    !ground.solid &&
    ground.normal[2] >= 0.7;
  if (s.grounded) {
    s.p[2] -= 2 * ground.fraction;
    s.v[2] = 0;
  }
  const touching = world.map.triggers.filter((t) =>
    touchesTrigger(s.p, t, height),
  );
  const ladder = touching.some((t) => t.type === "ladder"),
    water = touching.some((t) => t.type === "water");
  const jump = input.jump && (movement.autoBhop || !s.lastJump);
  if (jump && s.grounded) {
    s.v[2] = movement.jump;
    s.grounded = false;
    s.jumps++;
    for (const t of touching)
      if (t.type === "booster" && t.onJump && t.force) s.v = add(s.v, t.force);
  }
  s.lastJump = input.jump;
  if (s.grounded) {
    const current = speed(s.v);
    if (current) {
      const factor =
        Math.max(
          0,
          current -
            Math.max(current, movement.stopSpeed) * movement.friction * DT,
        ) / current;
      s.v[0] *= factor;
      s.v[1] *= factor;
    }
  }
  const c = Math.cos(input.yaw),
    sn = Math.sin(input.yaw);
  let wish: Vec3 = [c * input.f - sn * input.s, sn * input.f + c * input.s, 0];
  const magnitude = Math.hypot(wish[0], wish[1]);
  if (magnitude > 0) wish = scale(wish, 1 / magnitude);
  const wishSpeed =
    movement.maxWishSpeed *
    Math.min(1, magnitude) *
    (s.duck && s.grounded ? 0.34 : 1);
  accelerate(
    s.v,
    wish,
    wishSpeed,
    s.grounded ? movement.groundAcceleration : movement.airAcceleration,
    s.grounded ? wishSpeed : Math.min(movement.airWishCap, wishSpeed),
  );
  if (ladder) {
    s.v[2] = input.f * 180 + (input.jump ? 180 : 0);
    s.v[0] *= 0.8;
    s.v[1] *= 0.8;
  } else if (water) {
    s.v = scale(s.v, 0.98);
    s.v[2] += ((input.jump ? 240 : 0) - movement.gravity * 0.25) * DT;
  } else s.v[2] -= (movement.gravity * DT) / 2;
  const before = clone(s.p);
  let moved = slide(world, clone(s.p), clone(s.v), height, s.tick);
  if (s.grounded && speed(s.v) > 0) {
    const up = world.trace(s.p, [0, 0, movement.stepHeight], height, s.tick);
    const stepped = slide(
      world,
      add(s.p, [0, 0, movement.stepHeight * up.fraction]),
      clone(s.v),
      height,
      s.tick,
    );
    const down = world.trace(
      stepped.p,
      [0, 0, -movement.stepHeight],
      height,
      s.tick,
    );
    if (down.normal[2] >= 0.7) {
      stepped.p[2] -= movement.stepHeight * down.fraction;
      stepped.v[2] = 0;
      if (
        Math.hypot(stepped.p[0] - s.p[0], stepped.p[1] - s.p[1]) >
        Math.hypot(moved.p[0] - s.p[0], moved.p[1] - s.p[1])
      )
        moved = stepped;
    }
  }
  s.p = moved.p;
  s.v = moved.v;
  if (!ladder && !water) s.v[2] -= (movement.gravity * DT) / 2;
  // Quantization is part of the replay protocol.
  s.p = s.p.map((v) => Math.round(v * 1e6) / 1e6) as Vec3;
  s.v = s.v.map((v) => Math.round(v * 1e6) / 1e6) as Vec3;
  s.distance += Math.hypot(s.p[0] - before[0], s.p[1] - before[1]);
  s.maxSpeed = Math.max(s.maxSpeed, speed(s.v));
  const now = world.map.triggers.filter((t) => touchesTrigger(s.p, t, height));
  s.trigger = now.map((t) => t.id).join(",");
  if (now.some((t) => t.type === "start")) s.startArmed = true;
  if (
    s.startTick < 0 &&
    s.startArmed &&
    !now.some((t) => t.type === "start") &&
    !input.reset
  )
    s.startTick = s.tick;
  let failed = !!input.reset;
  for (const t of now) {
    if (
      t.type === "checkpoint" &&
      s.startTick >= 0 &&
      s.finishTick < 0 &&
      (!world.map.checkpoints_required || t.stage === s.stage + 1) &&
      !s.splits.some((split) => split.id === t.id)
    ) {
      s.splits.push({ id: t.id, stage: t.stage, tick: s.tick - s.startTick });
      s.stage = Math.max(s.stage, t.stage ?? s.stage);
      s.checkpoint = clone(t.target ?? s.p);
    }
    if (t.type === "teleport" && t.target) {
      if (t.failure) {
        s.deaths++;
        failed = true;
      }
      s.p = clone(t.target);
      s.v = [0, 0, 0];
    }
    if (
      t.type === "booster" &&
      !t.onExit &&
      !t.onJump &&
      t.force &&
      !touching.includes(t)
    )
      s.v = add(s.v, t.force);
  }
  for (const t of touching)
    if (t.type === "booster" && t.onExit && t.force && !now.includes(t))
      s.v = add(s.v, t.force);
  if (s.p[2] < world.map.killZ) {
    failed = true;
    s.deaths++;
    s.p = clone(s.checkpoint);
    s.v = [0, 0, 0];
    s.grounded = false;
  }
  // Evaluate at the final position, including legitimate map teleports. Completion is latched until spawn/restart.
  if (
    !failed &&
    s.finishTick < 0 &&
    s.startTick >= 0 &&
    s.tick > s.startTick &&
    (!world.map.checkpoints_required || s.stage >= world.map.stages - 1) &&
    world.map.triggers.some(
      (t) => t.type === "end" && touchesTrigger(s.p, t, height),
    )
  )
    s.finishTick = s.tick;
  return s;
}
